<template>
  <UIDialog
    :is-open
    :z-index
    size="lg"
    :title="`Deleted ${props.plural}`"
    flat
    @close="handleClose"
  >
    <div class="assets-deleted__content">
      <UIPanel
        v-model="clearedSearch"
        v-model:storage="searchStorage"
        :placeholder="`Search ${props.plural}...`"
        class="assets-deleted__panel"
      >
        <template #default="{ ButtonExport, size }">
          <UIFilter
            v-model="clearedFilter"
            v-model:storage="filterStorage"
            v-bind="{ size }"
            :fields="filterFields"
            flat
          />
          <UISort
            v-model="clearedSort"
            v-model:storage="sortStorage"
            v-bind="{ size }"
            :fields="sortFields"
          />
          <component
            :is="ButtonExport"
            v-if="selectedItems.length"
            @click="handleExport"
          />
        </template>
      </UIPanel>
      <UIGridSkeleton
        v-if="loading"
        :columns
        :sm
        selectable
        message="Loading assets..."
      />
      <UIGrid
        v-else
        ref="gridRef"
        v-model:selected="selectedItems"
        v-model:sort="sortStorage"
        :columns
        :sm
        :items="outputItems"
        selectable
        scrollable
      />
    </div>
    <template v-if="!!outputItems?.length" #footer>
      <UIButtonsGroup class="assets-deleted__footer">
        <UIButton
          v-if="selectedItems.length > 0"
          :leading="ArrowPathIcon"
          label="Restore"
          fill="light"
          @click="onRestoreSelectedClick"
        />
        <UIButton
          :label="emptyButtonLabel"
          :leading="TrashIcon"
          variant="danger"
          fill="light"
          @click="onEmptyTrashClick"
        />
      </UIButtonsGroup>
    </template>
  </UIDialog>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue'
import { upperFirst } from 'lodash'

import {
  Asset,
  Filter,
  FilterGroup,
  GridColumn,
  ModalClass,
  Sort,
} from '@types'

import { NOTIFICATION_DELAY } from '@/const'

import { useNotifications } from '@/plugins/notification'

import { handleCatchedError } from '@/helpers/common'

import useGridFilter from '@/components/hooks/gridFilter'
import useGridSort from '@/components/hooks/gridSort'
import { useGridCheckedExport } from '@/components/hooks/gridExport'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsStore } from '@/store/assets/index'
import { useAssetsDeletedStore } from '@/store/assets/deleted'
import { useLinkedDataStore } from '@/store/linkedData'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsDeletedStore } from '@/store/transactions/deleted'

import { UIButton, UIButtonsGroup } from '@ui/buttons'
import { UIPanel } from '@ui/core'
import { UIFilter, UISort } from '@ui/controllers'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UIDialog } from '@ui/modals'
import { TrashIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'

type Props = {
  modal: ModalClass<any>
  columns: GridColumn[]
  items: Asset[]
  plural: string
  loading?: boolean
}

const props = defineProps<Props>()

const analyticsStore = useAnalyticsStore()
const assetsStore = useAssetsStore()
const assetsDeletedStore = useAssetsDeletedStore()
const linkedDataStore = useLinkedDataStore()
const transactionsStore = useTransactionsStore()
const transactionsDeletedStore = useTransactionsDeletedStore()

const { warn, progress, update } = useNotifications()

const gridRef = useTemplateRef('gridRef')

const selectedItems = ref<string[]>([])

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const clearedFilter = ref<FilterGroup>()
const filterStorage = ref<Filter>()
const sortStorage = ref<Sort[]>()
const clearedSort = ref<Sort[]>()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const capitalizedPlural = computed(() => upperFirst(props.plural))

const emptyButtonLabel = computed(() =>
  selectedItems.value.length > 0 ? 'Delete forever' : 'Empty trash',
)

const inputItems = computed(() => props.items)

const sm = '1.4fr 1fr 1.4fr'
const columns = computed(() => props.columns)

const filteredItems = useGridFilter(
  columns,
  inputItems,
  clearedSearch,
  clearedFilter,
)
const outputItems = useGridSort(clearedSort, columns, filteredItems)

const filterFields = computed(() =>
  columns.value.map(column => ({
    name: column.name,
    caption: column.caption,
    ...column.filter,
  })),
)

const sortFields = computed(() =>
  columns.value.map(column => ({
    value: column.name,
    label: column.caption,
  })),
)

const onEmptyTrashClick = async () => {
  await warn({
    message: 'Warning',
    description: `This action will permanently delete the ${props.plural} and all transactions referencing the ${props.plural}. You will not be able to restore them`,
    actionRequired: true,
    actions: [
      {
        label: 'Cancel',
        buttonVariant: 'gray',
        buttonFill: 'outlined',
        onClick: remove => {
          remove && remove()
        },
      },
      {
        label:
          selectedItems.value.length > 0 ? 'Delete selected' : 'Delete all',
        buttonVariant: 'danger',
        onClick: async selfRemove => {
          selfRemove && selfRemove()
          try {
            const nid = await progress({
              message: `Hard delete ${props.plural}`,
            })
            const itemsToDelete =
              selectedItems.value.length > 0
                ? inputItems.value.filter(item =>
                  selectedItems.value.includes(item.id),
                )
                : inputItems.value
            const idsToDelete = itemsToDelete.map(item => item.id)
            await assetsDeletedStore.permanentDelete(idsToDelete)
            selectedItems.value = []
            assetsDeletedStore.setList(
              assetsDeletedStore.getList.filter(
                item => !idsToDelete.includes(item.id),
              ),
            )
            assetsStore.fetch()
            linkedDataStore.fetch()
            transactionsDeletedStore.clear()
            await update(
              nid,
              {
                type: 'success',
                message: `${capitalizedPlural.value} deleted`,
              },
              NOTIFICATION_DELAY,
            )
          } catch (e) {
            handleCatchedError(e as string)
          }
        },
      },
    ],
  })
}

const onRestoreSelectedClick = async () => {
  try {
    const nid = await progress({
      message: `Restoring ${props.plural}`,
    })
    const idsToRestore = inputItems.value
      .filter(item => selectedItems.value.includes(item.id))
      .map(item => item.id)
    await assetsDeletedStore.bulkRestore(idsToRestore)
    selectedItems.value = []
    analyticsStore.markAsDeprecated()
    await assetsStore.fetch()
    transactionsStore.clear()
    transactionsDeletedStore.clear()
    await update(
      nid,
      {
        type: 'success',
        message: `${capitalizedPlural.value} restored`,
      },
      NOTIFICATION_DELAY,
    )
  } catch (e) {
    handleCatchedError(e as string)
  }
}

const handleExport = () => {
  if (!gridRef.value) return
  useGridCheckedExport(columns.value, outputItems.value, selectedItems.value, 'deleted_assets', true)
  gridRef.value.deselect()
}

const handleClose = () => {
  props.modal.close()
}
</script>

<style scoped>
.assets-deleted {
  &__content {
    @apply flex flex-col h-96 gap-4;
  }

  &__panel {
    @apply pt-4 px-default;
  }

  &__footer {
    @apply py-4 px-default;
  }
}
</style>
